import React from 'react'
import { useQuery } from '@apollo/client'
import Button from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import LinearProgress from '@material-ui/core/LinearProgress'
import Typography from '@material-ui/core/Typography'
import KeyboardArrowDownRoundedIcon from '@material-ui/icons/KeyboardArrowDownRounded'
import KeyboardArrowUpRoundedIcon from '@material-ui/icons/KeyboardArrowUpRounded'
import CancelRoundedIcon from '@material-ui/icons/CancelRounded'
import CheckCircleRoundedIcon from '@material-ui/icons/CheckCircleRounded'
import i18n from 'components/i18n'
import { BulkUserJob } from 'mutations/bulk_user_job'
import { GET_BULK_USER_JOBS_LIST, removeBulkUserJob } from 'subscriptions/bulkUserJob'
import BulkDisableResultModal from './BulkDisableResultModal'

const ProgressWithLabel = (props: {value: number}) => (
  <Box display="flex" gridGap={1} alignItems="center">
    <Box width="100%" mr={1}>
      <LinearProgress variant="determinate" value={props.value} />
    </Box>
    <Box className="ml-1" minWidth={35}>
      <Typography variant="body2" color="textSecondary">{`${Math.round(props.value)}%`}</Typography>
    </Box>
    </Box>
)

const JobRow = (props: {job: BulkUserJob, onOpen: (job: BulkUserJob) => void}) => {
  const {job} = props
  const done = job.status === 'done'
  const remaining = Math.max(0, job.total - job.processed)

  return <li className="border flex flex-row justify-between items-center gap-2 py-2 px-3">
    <div className="flex-1 min-w-0">
      <div className="text-sm font-semibold truncate">
        {job.action === 'disable'
          ? i18n.t('bulk_progress_suspending', {count: job.total})
          : i18n.t('bulk_progress_enabling', {count: job.total})}
      </div>
      {done
        ? <button
            className="text-xs underline text-listenblue hover:text-listenblue-darker"
            onClick={() => props.onOpen(job)}
          >
            {i18n.t('bulk_progress_view_summary')}
          </button>
        : <div className="text-xs text-medgray">
            {i18n.t('bulk_progress_remaining', {
              processed: job.processed, total: job.total, remaining,
            })}
          </div>}
    </div>
    <div className="w-28">
      {done
        ? <div className="flex items-center justify-end text-listenblue">
            <CheckCircleRoundedIcon fontSize="small" />
          </div>
        : <ProgressWithLabel value={job.progress} />}
    </div>
    <button
      className="text-medgray"
      title={i18n.t('dismiss') || ''}
      onClick={() => removeBulkUserJob(job.id)}
    >
      <CancelRoundedIcon fontSize="small" className="cursor-pointer" />
    </button>
  </li>
}

/**
 * Persistent progress for bulk suspend/enable jobs, mounted globally so a running job
 * stays visible after the admin closes the result modal or navigates elsewhere.
 * Sibling of CheckFileImportProgress; deliberately separate because that component is
 * typed to FileImport throughout.
 */
export default function CheckBulkUserJobProgress() {
  const [open, setOpen] = React.useState(true)
  const [summaryJob, setSummaryJob] = React.useState<BulkUserJob|null>(null)
  const {data} = useQuery<{bulkUserJobsList: BulkUserJob[]}>(GET_BULK_USER_JOBS_LIST, {
    fetchPolicy: 'cache-only',
  })

  const jobs = data?.bulkUserJobsList || []
  if (jobs.length === 0) return null

  const running = jobs.filter(j => j.status !== 'done').length

  return <>
    <BulkDisableResultModal
      open={!!summaryJob}
      job={summaryJob}
      onClose={() => setSummaryJob(null)}
    />
    <div className="bg-white w-96 rounded-lg shadow-md">
      <Button
        onClick={() => setOpen(prev => !prev)}
        className="cursor-pointer rounded-b-none flex items-center justify-between h-11 w-full bg-listenblue text-white"
      >
        <div className="flex-1 text-left text-sm normal-case">
          {running > 0
            ? i18n.t('bulk_progress_running', {count: running})
            : i18n.t('bulk_progress_finished', {count: jobs.length})}
        </div>
        <div>{open ? <KeyboardArrowDownRoundedIcon /> : <KeyboardArrowUpRoundedIcon />}</div>
      </Button>
      {open && <ul>
        {jobs.map(job => (
          <JobRow key={job.id} job={job} onOpen={setSummaryJob} />
        ))}
      </ul>}
    </div>
  </>
}
